import axios from "axios";
import * as skedApiAll from "api/_skedApi";
import forEach from "lodash/forEach";
import isArray from "lodash/isArray";
import {
  GeneratedSkedApiClient,
  type OpenApiResponse,
} from "../generated-client";
import type { paths } from "../generated/sked-api-openapi-types";

let skedApi = skedApiAll.skedApi;

type TagValue = { value: string };
type MetaPrimitive = string | number | boolean | null | undefined;
type MetaValue =
  MetaPrimitive | MetaPrimitive[] | Record<string, MetaPrimitive>;
type EntryMeta = Record<string, MetaValue>;
type ListInboxesOperation = paths["/content-inbox/inbox"]["get"];
type GetAssetsByInboxIdOperation =
  paths["/content-inbox/inbox/{inboxId}/assets"]["get"];
type CreateInboxBody = NonNullable<
  paths["/content-inbox/inbox"]["post"]["requestBody"]
>["content"]["application/json"];
type CreateAssetBody = NonNullable<
  paths["/content-inbox/inbox/{inboxId}/upload"]["post"]["requestBody"]
>["content"]["application/json"];
type UpdateInboxBody = NonNullable<
  paths["/content-inbox/inbox/{inboxId}"]["put"]["requestBody"]
>["content"]["application/json"];
type UpdateAssetBody = NonNullable<
  paths["/content-inbox/{assetId}"]["put"]["requestBody"]
>["content"]["application/json"];
type DownloadAssetByIdResponse =
  paths["/content-inbox/{assetId}/download"]["get"]["responses"][200]["content"]["application/json"];
type AddInboxAuthBody = NonNullable<
  paths["/content-inbox/inbox/{inboxId}/auth"]["post"]["requestBody"]
>["content"]["application/json"];
type UpdateInboxAuthBody = NonNullable<
  paths["/content-inbox/inbox/{inboxId}/auth/{authId}"]["put"]["requestBody"]
>["content"]["application/json"];

function getGeneratedSkedApiClient() {
  return new GeneratedSkedApiClient<paths>();
}

function isUsingLegacySkedApiOverride() {
  return skedApi !== skedApiAll.skedApi;
}

async function toAxiosResponse<T>(
  generatedClient: GeneratedSkedApiClient<paths>,
  response: Promise<OpenApiResponse<T>>
): Promise<{ data: T }> {
  return { data: await generatedClient.unwrap(await response) };
}

export function setMediaLibrarySkedApiInLegacyApi(
  customSkedApi: typeof skedApiAll.skedApi
) {
  skedApi = customSkedApi;
}
export default class MediaLibraryLegacyApi {
  baseUrl: string;

  constructor() {
    this.baseUrl = `/content-inbox`;
  }

  // --------- COLLECTIONS ---------- //
  getCollections = () => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.get(`/content-inbox/inbox?_limit=900&_sort=name`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET("/content-inbox/inbox", {
        params: {
          query: {
            _limit: 900,
            _sort: "name",
          } as ListInboxesOperation["parameters"]["query"] & {
            _limit: number;
            _sort: string;
          },
        },
      })
    );
  };

  getCollectionsMetadata = () => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.get(`/content-inbox/inbox/metadata?_limit=900&_sort=name`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET("/content-inbox/inbox/metadata", {
        params: {
          query: {
            _limit: 900,
            _sort: "name",
          },
        } as never,
      })
    );
  };

  getCollectionCounts = () => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.get(`/content-inbox/inbox/counts?_limit=900&_sort=name`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET("/content-inbox/inbox/counts", {
        params: {
          query: {
            _limit: 900,
            _sort: "name",
          },
        } as never,
      })
    );
  };

  createCollection = (name: string, ext_auth: unknown) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.post(`/content-inbox/inbox`, {
        name,
        ext_auth,
      });
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.POST("/content-inbox/inbox", {
        body: {
          name,
          ext_auth,
        } as CreateInboxBody,
      })
    );
  };

  deleteCollection = (id: string) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.delete(`/content-inbox/inbox/${id}`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.DELETE("/content-inbox/inbox/{inboxId}", {
        params: {
          path: { inboxId: id },
        },
      })
    );
  };

  updateCollection = (id: string, update: UpdateInboxBody) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.put(`/content-inbox/inbox/${id}`, update);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.PUT("/content-inbox/inbox/{inboxId}", {
        params: {
          path: { inboxId: id },
        },
        body: update,
      })
    );
  };

  getLibraryUsage = () => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.get(`/content-inbox/entry-stats`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET("/content-inbox/entry-stats")
    );
  };

  // --------- COLLECTION TAGS ---------- //
  getTags = (collectionId: string) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.get(`/content-inbox/inbox/${collectionId}/tags`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET("/content-inbox/inbox/{inboxId}/tags", {
        params: {
          path: { inboxId: collectionId },
        },
      })
    );
  };

  // --------- ENTRIES (Assets) ---------- //
  getEntries = (
    collectionId: string,
    page = 1,
    pageSize = 25,
    tags: TagValue[] = [],
    name = ``,
    description = ``,
    favorite = false,
    mediaType: string | null = null,
    sourceName = ``,
    usage: string | null = null,
    withDownloadUrl = true,
    sort = "-created"
  ) => {
    if (isUsingLegacySkedApiOverride()) {
      const queryParts = [
        `_page=${page}`,
        `_limit=${pageSize}`,
        `_sort=${encodeURIComponent(sort)}`,
        `withDownloadUrl=${withDownloadUrl}`,
      ];
      if (tags.length > 0) {
        forEach(tags, (tag: TagValue) => {
          queryParts.push(`tags[]=${encodeURIComponent(tag.value)}`);
        });
      }

      if (name.length > 0) {
        queryParts.push(`name=${encodeURIComponent(name)}`);
      }
      if (description.length > 0) {
        queryParts.push(`description=${encodeURIComponent(description)}`);
      }
      if (sourceName.length > 0) {
        queryParts.push(`sourceName=${encodeURIComponent(sourceName)}`);
      }
      if (usage) {
        queryParts.push(`usage=${encodeURIComponent(usage)}`);
      }
      // if false, do not pass it in  as we will then show all assets
      if (favorite) {
        queryParts.push(`favorite=true`);
      }

      if (mediaType) {
        queryParts.push(`mediaType=${mediaType}`);
      }

      const queryString = queryParts.join(`&`);
      return skedApi.get(
        `/content-inbox/inbox/${collectionId}/assets?${queryString}`
      );
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET("/content-inbox/inbox/{inboxId}/assets", {
        params: {
          path: { inboxId: collectionId },
          query: {
            _page: page,
            _limit: pageSize,
            _sort: sort,
            withDownloadUrl,
            "tags[]": tags.map((tag) => tag.value),
            name: name || undefined,
            description: description || undefined,
            sourceName: sourceName || undefined,
            usage: usage || undefined,
            favorite: favorite || undefined,
            mediaType: mediaType || undefined,
          } as GetAssetsByInboxIdOperation["parameters"]["query"] & {
            _page: number;
            _limit: number;
            _sort: string;
            withDownloadUrl: boolean;
            description?: string;
            sourceName?: string;
            usage?: string;
            favorite?: boolean;
            mediaType?: string;
          },
        },
      })
    );
  };

  addEntryFromUrl = (collectionId: string, body: object) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.post(`/content-inbox/inbox/${collectionId}/upload`, body);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.POST("/content-inbox/inbox/{inboxId}/upload", {
        params: {
          path: { inboxId: collectionId },
        },
        body: body as CreateAssetBody,
      })
    );
  };

  addEntry = (collectionId: string, file: Blob, meta: EntryMeta) => {
    const formData = this.convertToFormData(file, meta);
    return skedApi.post(
      `/content-inbox/inbox/${collectionId}/upload`,
      formData,
      {
        headers: {
          "Content-Type": `multipart/form-data`,
        },
      }
    );
  };

  getEntry = (assetId: string) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.get(`/content-inbox/${assetId}`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET("/content-inbox/{assetId}", {
        params: {
          path: { assetId },
        },
      })
    );
  };

  downloadEntry = async (assetId: string) => {
    let file: DownloadAssetByIdResponse;

    if (isUsingLegacySkedApiOverride()) {
      const response = await skedApi.get<DownloadAssetByIdResponse>(
        `/content-inbox/${assetId}/download`
      );
      file = response.data;
    } else {
      const generatedClient = getGeneratedSkedApiClient();
      const result = await generatedClient.client.GET(
        "/content-inbox/{assetId}/download",
        {
          params: {
            path: { assetId },
          },
        }
      );
      file = await generatedClient.unwrap(result);
    }

    return await axios.get(file.url, {
      responseType: "blob",
    });
  };

  updateEntry = (
    assetId: string,
    name: string,
    description: string,
    tags: string[],
    file: Blob | null | undefined,
    source: string,
    _sourceNotes: unknown,
    favorite: boolean | undefined
  ) => {
    const putObject: {
      name: string;
      description: string;
      tags: string[];
      source: string;
      favorite?: boolean;
    } = {
      name,
      description,
      tags,
      source,
      favorite,
    };
    if (favorite !== undefined) {
      putObject.favorite = favorite;
    }
    const putUrl = `/content-inbox/${assetId}`;
    if (!file) {
      if (isUsingLegacySkedApiOverride()) return skedApi.put(putUrl, putObject);

      const generatedClient = getGeneratedSkedApiClient();
      return toAxiosResponse(
        generatedClient,
        generatedClient.client.PUT("/content-inbox/{assetId}", {
          params: {
            path: { assetId },
          },
          body: putObject as UpdateAssetBody,
        })
      );
    }
    const formData = this.convertToFormData(file, putObject as EntryMeta);

    return skedApi.put(putUrl, formData, {
      headers: {
        "Content-Type": `multipart/form-data`,
      },
    });
  };

  moveEntry = (assetId: string, collectionId: string) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.put(`/content-inbox/${assetId}`, {
        inbox_id: collectionId,
      });
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.PUT("/content-inbox/{assetId}", {
        params: {
          path: { assetId },
        },
        body: {
          inbox_id: collectionId,
        } as UpdateAssetBody,
      })
    );
  };

  updateEntryMeta = (assetId: string, payload: object) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.put(`/content-inbox/${assetId}`, payload);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.PUT("/content-inbox/{assetId}", {
        params: {
          path: { assetId },
        },
        body: payload as UpdateAssetBody,
      })
    );
  };

  deleteEntry = (assetId: string) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.delete(`/content-inbox/${assetId}`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.DELETE("/content-inbox/{assetId}", {
        params: {
          path: { assetId },
        },
      })
    );
  };

  // --------- USERS (AUTH) ---------- //
  // TODO review the use of this and port to API
  getCollectionAuth = (collectionId: string) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.get(`/content-inbox/inbox/${collectionId}/auth`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET("/content-inbox/inbox/{inboxId}/auth", {
        params: {
          path: { inboxId: collectionId },
        },
      })
    );
  };

  createCollectionAuthUser = (collectionId: string, payload: object) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.post(`/content-inbox/inbox/${collectionId}/auth`, payload);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.POST("/content-inbox/inbox/{inboxId}/auth", {
        params: {
          path: { inboxId: collectionId },
        },
        body: payload as AddInboxAuthBody,
      })
    );
  };

  getCollectionAuthUser = (collectionId: string, authId: string) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.get(`/content-inbox/inbox/${collectionId}/auth/${authId}`);
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.GET(
        "/content-inbox/inbox/{inboxId}/auth/{authId}",
        {
          params: {
            path: { inboxId: collectionId, authId },
          },
        }
      )
    );
  };

  updateCollectionAuthUser = (
    collectionId: string,
    authId: string,
    payload: object
  ) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.put(
        `/content-inbox/inbox/${collectionId}/auth/${authId}`,
        payload
      );
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.PUT(
        "/content-inbox/inbox/{inboxId}/auth/{authId}",
        {
          params: {
            path: { inboxId: collectionId, authId },
          },
          body: payload as UpdateInboxAuthBody,
        }
      )
    );
  };

  deleteCollectionAuthUser = (
    collectionId: string,
    authId: string,
    payload: object
  ) => {
    if (isUsingLegacySkedApiOverride()) {
      return skedApi.delete(
        `/content-inbox/inbox/${collectionId}/auth/${authId}`,
        payload
      );
    }

    const generatedClient = getGeneratedSkedApiClient();
    return toAxiosResponse(
      generatedClient,
      generatedClient.client.DELETE(
        "/content-inbox/inbox/{inboxId}/auth/{authId}",
        {
          params: {
            path: { inboxId: collectionId, authId },
          },
        }
      )
    );
  };

  // --------- HELPERS ---------- //
  convertToFormData = (file: Blob, meta: EntryMeta) => {
    const formData = new FormData();
    formData.append(`files[]`, file);
    Object.keys(meta).forEach((k) => {
      const value = meta[k];
      if (isArray(value)) {
        forEach(value, (arrayItem: MetaPrimitive) => {
          formData.append(`${k}[]`, String(arrayItem ?? ""));
        });
        return;
      }
      if (value && typeof value === "object") {
        Object.keys(value).forEach((k2) => {
          const nestedValue = value[k2];
          if (nestedValue !== null) {
            formData.append(`${k}.${k2}`, String(nestedValue || ""));
          }
        });
        return;
      }
      formData.append(k, String(value ?? ""));
    });
    return formData;
  };
}
